import { useRef, useState, type ReactNode } from "react";
import { AlarmClock, ChevronRight, PanelLeft, Plus, Search, Settings } from "lucide-react";
import type { NavItem, ShellProps, SidebarAreaNode, SidebarDnd, SidebarDropState, SidebarProjectNode, SidebarRename, SidebarSpaceNode } from "../types";
import { ProjectGlyph, DeskMark, SpaceGlyph } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/misc";
import { formatInstant, formatWake, localZone } from "@/lib/dates";
import { cn, modKey } from "@/lib/utils";
import { ContainerGlyph, SpaceIcon } from "@/components/container-icon";
import { useStored } from "@/lib/storage";
import { SidebarOverlay } from "@/components/sidebar-overlay";

const rowBase =
  "group/item relative flex h-[30px] items-center gap-2 rounded-md pl-2 pr-1 text-[14px] text-sidebar-foreground outline-none transition-colors hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring/40 has-[a:focus-visible]:ring-2 has-[a:focus-visible]:ring-ring/40";

function NodeActions({ children, active }: { children: React.ReactNode; active?: boolean }) {
  return (
    <span
      className={cn(
        "ml-auto flex items-center opacity-0 transition-opacity group-hover/item:opacity-100 group-has-[:focus-visible]/item:opacity-100 has-[[data-state=open]]:opacity-100",
        active && "opacity-0",
      )}
    >
      {children}
    </span>
  );
}

/**
 * Right edge of a space/project row: a quiet open-task count (or wake time) that swaps for the hover
 * actions on hover or keyboard focus. An open menu keeps the actions visible. Swapping, rather than
 * reserving room for invisible buttons, keeps long names from truncating early.
 */
function Trailing({ meta, actions }: { meta: React.ReactNode; actions: React.ReactNode }) {
  const showActions = "group-hover/item:flex group-has-[:focus-visible]/item:flex group-has-[[data-state=open]]/item:flex";
  const hideMeta = "group-hover/item:hidden group-has-[:focus-visible]/item:hidden group-has-[[data-state=open]]/item:hidden";
  return (
    <>
      {meta !== null && meta !== undefined && meta !== false && (
        <span className={cn("shrink-0 whitespace-nowrap pl-1 pr-1.5 text-[12px] text-subtle tabular", hideMeta)}>{meta}</span>
      )}
      {/* Rename/Archive/New project stay in the page header's "…" menu; the row keeps the two quick actions. */}
      <span data-paper-node-actions className={cn("hidden shrink-0 items-center [&_button[aria-label^='More_actions_for']]:hidden", showActions)}>
        {actions}
      </span>
    </>
  );
}

/** Without drag support (a shell rendered outside the drag provider), rows are plain. */
const NO_DND: SidebarDnd = {
  useRow: () => ({ setNodeRef: () => {}, setActivatorNodeRef: () => {}, listeners: {}, isDragging: false, drop: null }),
  useNavTarget: () => ({ setNodeRef: () => {}, drop: null }),
  useTopLevelZone: () => ({ setNodeRef: () => {}, drop: null, visible: false }),
};

/** "Move into" fills the row; a reorder draws a line where the row will land. */
const dropRow = (drop: SidebarDropState) => drop === "into" && "bg-selected ring-2 ring-inset ring-ring/50";

function DropLine({ drop }: { drop: SidebarDropState }) {
  if (drop !== "before" && drop !== "after") return null;
  return (
    <span
      aria-hidden
      className={cn("pointer-events-none absolute inset-x-1 z-10 h-[2px] rounded-full bg-ring", drop === "before" ? "-top-px" : "-bottom-px")}
    />
  );
}

/** Name field for double-click rename. Enter or blur saves; Escape cancels without a request. */
function InlineRename({ value, rename, onDone }: { value: string; rename: SidebarRename; onDone: () => void }) {
  const finished = useRef(false);
  const finish = (next: string | null) => {
    if (finished.current) return;
    finished.current = true;
    onDone();
    const v = next?.trim();
    if (v && v !== value) rename.onRename(v);
  };
  return (
    <input
      autoFocus
      aria-label="Name"
      defaultValue={value}
      maxLength={rename.maxLength}
      onFocus={(e) => e.currentTarget.select()}
      onPointerDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter") {
          e.preventDefault();
          finish(e.currentTarget.value);
        } else if (e.key === "Escape") {
          e.preventDefault();
          finish(null);
        }
      }}
      onBlur={(e) => finish(e.currentTarget.value)}
      className="desk-no-drag h-6 min-w-0 flex-1 rounded-[5px] bg-background px-1.5 text-[inherit] text-foreground outline-none ring-1 ring-ring/60"
    />
  );
}

/** The row's link (icon + name); double-click switches the name to an inline field. */
function RowLink({
  href,
  active,
  name,
  icon,
  rename,
  linkRef,
  nameClassName,
}: {
  href: string;
  active: boolean;
  name: string;
  icon: ReactNode;
  rename?: SidebarRename;
  linkRef: (el: HTMLElement | null) => void;
  nameClassName?: string;
}) {
  const [editing, setEditing] = useState(false);
  if (editing && rename)
    return (
      <span className="flex min-w-0 flex-1 items-center gap-2">
        {icon}
        <InlineRename value={name} rename={rename} onDone={() => setEditing(false)} />
      </span>
    );
  return (
    <a
      ref={linkRef}
      href={href}
      className="flex min-w-0 flex-1 items-center gap-2 outline-none"
      aria-current={active ? "page" : undefined}
      onDoubleClick={
        rename
          ? (e) => {
              e.preventDefault();
              setEditing(true);
            }
          : undefined
      }
    >
      {icon}
      <span className={cn("truncate", nameClassName)}>{name}</span>
    </a>
  );
}

const withMenu = (slots: ShellProps["slots"], node: SidebarAreaNode | SidebarSpaceNode | SidebarProjectNode, row: ReactNode) =>
  slots.rowMenu ? slots.rowMenu(node, row) : row;

/** Indentation is the only hierarchy signal: every level steps in by the same amount. */
const INDENT = 18;
const indent = (depth: number) => (depth ? { marginLeft: depth * INDENT } : undefined);

function ProjectRow({ node, slots, depth }: { node: SidebarProjectNode; slots: ShellProps["slots"]; depth: number }) {
  const d = (slots.sidebarDnd ?? NO_DND).useRow(node);
  return withMenu(
    slots,
    node,
    <div
      ref={d.setNodeRef}
      {...d.listeners}
      style={indent(depth)}
      className={cn(rowBase, node.active && "bg-sidebar-accent font-medium text-foreground", d.isDragging && "opacity-40", dropRow(d.drop))}
    >
      <DropLine drop={d.drop} />
      {node.active && node.project.color && (
        <span aria-hidden className="absolute inset-y-1.5 left-0 w-[2px] rounded-full" style={{ background: node.project.color }} />
      )}
      <RowLink
        href={node.href}
        active={node.active}
        name={node.project.name}
        icon={<ProjectGlyph state={node.project.state} progress={node.progress} />}
        rename={node.rename}
        linkRef={d.setActivatorNodeRef}
        nameClassName="text-[13.5px]"
      />
      <Trailing meta={node.openCount || null} actions={slots.containerActions(node)} />
    </div>,
  );
}

function SpaceRow({ node, slots, depth = 0 }: { node: SidebarSpaceNode; slots: ShellProps["slots"]; depth?: number }) {
  const hasProjects = node.projects.length > 0;
  const d = (slots.sidebarDnd ?? NO_DND).useRow(node);
  return (
    <div>
      {withMenu(
        slots,
        node,
        <div
          ref={d.setNodeRef}
          {...d.listeners}
          style={indent(depth)}
          className={cn(rowBase, node.active && "bg-sidebar-accent font-medium text-foreground", d.isDragging && "opacity-40", dropRow(d.drop))}
        >
          <DropLine drop={d.drop} />
          {node.active && node.space.color && (
            <span aria-hidden className="absolute inset-y-1.5 left-0 w-[2px] rounded-full" style={{ background: node.space.color }} />
          )}
          <RowLink
            href={node.href}
            active={node.active}
            name={node.space.name}
            icon={<SpaceIcon space={node.space} />}
            rename={node.rename}
            linkRef={d.setActivatorNodeRef}
          />
          <Trailing meta={node.openCount || null} actions={slots.containerActions(node)} />
          {hasProjects && (
            <button
              type="button"
              onClick={node.onToggle}
              aria-label={node.expanded ? `Collapse ${node.space.name}` : `Expand ${node.space.name}`}
              aria-expanded={node.expanded}
              // Shown on hover/focus only, so space counts line up with project counts.
              className="hidden size-6 shrink-0 items-center justify-center rounded text-subtle hover:bg-sidebar-accent hover:text-foreground group-hover/item:inline-flex group-has-[:focus-visible]/item:inline-flex"
            >
              <ChevronRight className={cn("size-3.5 transition-transform duration-150", node.expanded && "rotate-90")} />
            </button>
          )}
        </div>,
      )}
      {hasProjects && node.expanded && (
        <div className="mt-px flex flex-col gap-px">
          {node.projects.map((p) => (
            <ProjectRow key={p.project.id} node={p} slots={slots} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * An area is a top-level row like a top-level space: its icon (or letter glyph) and name, with a
 * hover chevron to collapse it. Its spaces sit one indent step under it.
 */
function AreaBlock({ node, slots }: { node: SidebarAreaNode; slots: ShellProps["slots"] }) {
  const hasSpaces = node.spaces.length > 0;
  const d = (slots.sidebarDnd ?? NO_DND).useRow(node);
  return (
    <div>
      {withMenu(
        slots,
        node,
        <div
          ref={d.setNodeRef}
          {...d.listeners}
          className={cn(rowBase, node.active && "bg-sidebar-accent font-medium text-foreground", d.isDragging && "opacity-40", dropRow(d.drop))}
        >
          <DropLine drop={d.drop} />
          {node.active && node.area.color && (
            <span aria-hidden className="absolute inset-y-1.5 left-0 w-[2px] rounded-full" style={{ background: node.area.color }} />
          )}
          <RowLink
            href={node.href}
            active={node.active}
            name={node.area.name}
            icon={<ContainerGlyph of={node.area} fallback={<SpaceGlyph name={node.area.name} />} />}
            rename={node.rename}
            linkRef={d.setActivatorNodeRef}
          />
          <NodeActions>{slots.areaActions(node)}</NodeActions>
          {hasSpaces && (
            <button
              type="button"
              onClick={node.onToggle}
              aria-label={node.expanded ? `Collapse ${node.area.name}` : `Expand ${node.area.name}`}
              aria-expanded={node.expanded}
              className="hidden size-6 shrink-0 items-center justify-center rounded text-subtle hover:bg-sidebar-accent hover:text-foreground group-hover/item:inline-flex group-has-[:focus-visible]/item:inline-flex"
            >
              <ChevronRight className={cn("size-3.5 transition-transform duration-150", node.expanded && "rotate-90")} />
            </button>
          )}
        </div>,
      )}
      {hasSpaces && node.expanded && (
        <div className="mt-px flex flex-col gap-px">
          {node.spaces.map((s) => (
            <SpaceRow key={s.space.id} node={s} slots={slots} depth={1} />
          ))}
        </div>
      )}
    </div>
  );
}

function NavRow({ item, dnd }: { item: NavItem; dnd: SidebarDnd }) {
  const target = dnd.useNavTarget(item.id);
  return (
    <li ref={target.setNodeRef}>
      <a
        href={item.href}
        aria-current={item.active ? "page" : undefined}
        className={cn(rowBase, "pr-2", item.active && "bg-sidebar-accent font-medium text-foreground", dropRow(target.drop))}
      >
        <item.icon
          className={cn(
            "size-4 shrink-0",
            item.id === "today" ? "fill-[var(--star)] text-[var(--star)]" : item.active ? "text-foreground" : "text-foreground/55",
          )}
          strokeWidth={1.8}
        />
        <span className="truncate">{item.label}</span>
        {item.count !== undefined && item.count > 0 && (
          <span
            className={cn(
              "ml-auto text-[12px] tabular",
              item.id === "review" ? "rounded-full bg-foreground/10 px-1.5 font-medium text-foreground/80" : "text-subtle",
            )}
          >
            {item.count}
          </span>
        )}
      </a>
    </li>
  );
}

/** While a space in an area is dragged and there are no top-level spaces, this is where it can go. */
function TopLevelZone({ dnd, enabled }: { dnd: SidebarDnd; enabled: boolean }) {
  const zone = dnd.useTopLevelZone(enabled);
  if (!zone.visible) return <div ref={zone.setNodeRef} />;
  return (
    <div
      ref={zone.setNodeRef}
      className={cn(
        "flex h-[30px] items-center rounded-md border border-dashed border-foreground/20 px-2 text-[13px] text-subtle",
        dropRow(zone.drop),
      )}
    >
      Top level
    </div>
  );
}

const SIDEBAR_MIN = 220;
const SIDEBAR_MAX = 420;

/** Drag the sidebar's right edge to resize; the width is remembered. Double-click resets it. */
function useSidebarWidth(): [number, (e: React.PointerEvent) => void, () => void] {
  const [width, setWidth] = useStored("sidebar.width", 264);
  const start = (e: React.PointerEvent) => {
    e.preventDefault();
    const x0 = e.clientX;
    const w0 = width;
    const move = (ev: PointerEvent) => setWidth(Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, Math.round(w0 + ev.clientX - x0))));
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      document.body.style.removeProperty("cursor");
    };
    document.body.style.cursor = "col-resize";
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  return [width, start, () => setWidth(264)];
}

export function PaperShell({ sidebar, slots, sidebarCollapsed, sidebarOverlay, onToggleSidebar, detailPanel, children }: ShellProps) {
  const tz = localZone();
  const [width, startResize, resetWidth] = useSidebarWidth();
  return (
    <div className="flex h-full w-full overflow-hidden bg-background">
      {/* Inline, the sidebar stays mounted and slides out of view, so hiding it animates instead of jumping. */}
      {!(sidebarOverlay && sidebarCollapsed) && (
        <SidebarOverlay overlay={sidebarOverlay} onDismiss={onToggleSidebar}>
        <aside
          aria-label="Sidebar"
          inert={sidebarCollapsed}
          style={{ width, marginLeft: sidebarCollapsed ? -width : 0 }}
          className="relative flex shrink-0 flex-col bg-sidebar text-sidebar-foreground transition-[margin-left] duration-[var(--desk-duration)] ease-out-soft"
        >
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize sidebar"
            onPointerDown={startResize}
            onDoubleClick={resetWidth}
            className="desk-no-drag absolute inset-y-0 -right-1 z-10 w-2 cursor-col-resize"
          />
          <div className="desk-drag desk-titlebar flex h-12 shrink-0 items-center gap-1 pl-4 pr-2">
            <span className="flex items-center gap-1.5 font-display text-[17px] font-bold tracking-[-0.02em] text-foreground">
              <DeskMark className="size-[18px]" />
              Desk
            </span>
            <div className="desk-no-drag ml-auto flex items-center">
              <Tooltip content="Search" shortcut={`${modKey}K`}>
                <Button size="icon" variant="quiet" aria-label="Search" onClick={slots.onSearch}>
                  <Search />
                </Button>
              </Tooltip>
              <Tooltip content="New task" shortcut={`${modKey}N`}>
                <Button size="icon" variant="quiet" aria-label="New task" onClick={slots.onQuickAdd}>
                  <Plus />
                </Button>
              </Tooltip>
              <Tooltip content="Hide sidebar">
                <Button size="icon" variant="quiet" aria-label="Hide sidebar" onClick={onToggleSidebar}>
                  <PanelLeft />
                </Button>
              </Tooltip>
            </div>
          </div>
          <nav aria-label="Views" className="flex flex-1 flex-col overflow-y-auto px-2 pb-3">
            <ul className="flex flex-col gap-px">
              {sidebar.nav.map((item) => (
                <NavRow key={item.id} item={item} dnd={slots.sidebarDnd ?? NO_DND} />
              ))}
            </ul>

            {/* Every top-level group (a top-level space or an area, with its children) is separated by the same gap. */}
            {(sidebar.topSpaces.length > 0 || sidebar.areas.length > 0) && (
              <div className="mt-4 flex flex-col gap-2.5">
                <TopLevelZone dnd={slots.sidebarDnd ?? NO_DND} enabled={sidebar.topSpaces.length === 0} />
                {sidebar.topSpaces.map((s) => (
                  <SpaceRow key={s.space.id} node={s} slots={slots} />
                ))}
                {sidebar.areas.map((a) => (
                  <AreaBlock key={a.area.id} node={a} slots={slots} />
                ))}
              </div>
            )}

            <div className="mt-3">{slots.newArea}</div>

            {sidebar.settled.length > 0 && (
              <div className="mt-auto pt-6">
                <button
                  type="button"
                  onClick={sidebar.onToggleSettled}
                  aria-expanded={sidebar.settledExpanded}
                  className="flex h-7 w-full items-center gap-1.5 rounded-md px-2 text-[13px] font-semibold text-foreground/60 hover:bg-sidebar-accent hover:text-foreground"
                >
                  <ChevronRight className={cn("size-3.5 transition-transform", sidebar.settledExpanded && "rotate-90")} />
                  Settled
                  <span className="ml-auto font-normal text-subtle tabular">{sidebar.settled.length}</span>
                </button>
                {sidebar.settledExpanded && (
                  <div className="mt-0.5 flex flex-col gap-px">
                    {sidebar.settled.map((n) => (
                      <div key={n.kind === "space" ? n.space.id : n.project.id} className={cn(rowBase, n.active && "bg-sidebar-accent")}>
                        <a
                          href={n.href}
                          aria-current={n.active ? "page" : undefined}
                          className="flex min-w-0 flex-1 items-center gap-2 text-foreground/60 outline-none"
                        >
                          {n.kind === "space" ? <SpaceIcon space={n.space} className="opacity-70" /> : <ProjectGlyph state={n.project.state} progress={n.progress} className="opacity-70" />}
                          <span className="truncate text-[13.5px]">{n.kind === "space" ? n.space.name : n.project.name}</span>
                        </a>
                        <Trailing
                          meta={
                            n.snoozedUntil ? (
                              <span className="inline-flex items-center gap-1 text-[11.5px]" title={`Returns ${formatInstant(n.snoozedUntil, tz)}`}>
                                <AlarmClock className="size-3" aria-hidden />
                                <span className="sr-only">Returns </span>
                                {formatWake(n.snoozedUntil, tz)}
                              </span>
                            ) : null
                          }
                          actions={slots.containerActions(n)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </nav>
          <div className="flex h-11 shrink-0 items-center gap-1 px-2">
            <div className="min-w-0 flex-1">{slots.syncStatus}</div>
            <Tooltip content="Settings" shortcut={`${modKey},`}>
              <a
                href={slots.settingsHref}
                aria-label="Settings"
                aria-current={slots.settingsActive ? "page" : undefined}
                className={cn(
                  "inline-flex size-7 items-center justify-center rounded-md text-foreground/55 hover:bg-sidebar-accent hover:text-foreground",
                  slots.settingsActive && "bg-sidebar-accent text-foreground",
                )}
              >
                <Settings className="size-4" />
              </a>
            </Tooltip>
          </div>
        </aside>
        </SidebarOverlay>
      )}
      <main className="relative flex min-w-0 flex-1 flex-col">
        {/* Non-scrolling title bar: an empty window-drag strip, clear of the macOS traffic lights when the sidebar is hidden. */}
        <div className={cn("desk-drag flex h-12 shrink-0 items-center px-2", sidebarCollapsed && "desk-titlebar")}>
          {sidebarCollapsed && (
            <Tooltip content="Show sidebar">
              <Button size="icon" variant="quiet" aria-label="Show sidebar" onClick={onToggleSidebar}>
                <PanelLeft />
              </Button>
            </Tooltip>
          )}
        </div>
        <div id="desk-main-scroll" className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-[820px] px-6 pb-16 pt-1 has-[[data-wide]]:max-w-none sm:px-10">{children}</div>
        </div>
        <div className="flex h-11 shrink-0 items-center justify-end border-t border-border/60 px-3">{slots.toolbar}</div>
      </main>
      {detailPanel && <aside className="w-[420px] shrink-0 border-l border-border bg-card">{detailPanel}</aside>}
    </div>
  );
}
